import { Component, computed, input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ResourceTagDto } from '../../../models/resource.models';
import {
  DEFAULT_CHIP_LABEL_MAX_LENGTH,
  truncateChipLabel,
} from '../../../utils/chip-label.utils';

/** A tag pill prepared for rendering: its label is truncated with an ellipsis
 *  when longer than the limit, with the full text surfaced via tooltip. */
interface DisplayTag {
  /** Label as shown (truncated with an ellipsis when over the limit). */
  display: string;
  /** Full, untruncated label — shown in a tooltip when truncated. */
  full: string;
  truncated: boolean;
}

/**
 * Read-only list of profile tags for the resource list's tag columns
 * (Skills, Jurisdictions, Languages, Interests, Sub-Practice Areas).
 *
 * Pills look exactly like the Attributes card's tag labels (shared
 * attribute-tag-pill mixin) minus the × remove icon, with the same overflow
 * rules as app-membership-chips: everything past `maxVisible` rolls into a
 * "+N" pill (full list on hover), and labels longer than `maxLabelLength`
 * are truncated with the full text on hover.
 */
@Component({
  selector: 'app-attribute-tag-chips',
  standalone: true,
  imports: [MatTooltipModule],
  templateUrl: './attribute-tag-chips.component.html',
  styleUrls: ['./attribute-tag-chips.component.scss'],
})
export class AttributeTagChipsComponent {
  readonly tags = input<readonly ResourceTagDto[]>([]);
  readonly maxVisible = input<number>(2);
  /** Labels longer than this are truncated with an ellipsis (full text in a tooltip). */
  readonly maxLabelLength = input<number>(DEFAULT_CHIP_LABEL_MAX_LENGTH);

  private readonly names = computed<string[]>(() =>
    (this.tags() ?? [])
      .map((tag) => tag?.name ?? '')
      .filter((name) => name.length > 0),
  );

  readonly visible = computed<DisplayTag[]>(() => {
    const max = this.maxLabelLength();
    return this.names()
      .slice(0, this.maxVisible())
      .map((name) => {
        const display = truncateChipLabel(name, max);
        return { full: name, display, truncated: display !== name };
      });
  });

  readonly hidden = computed(() => this.names().slice(this.maxVisible()));
  readonly hiddenCount = computed(() => this.hidden().length);
  readonly tooltipText = computed(() => this.hidden().join('\n'));
  readonly hasAny = computed(() => this.names().length > 0);
}
